// Chord shapes for fretted instruments and piano tones (F4.4, F4.8, F4.10; design: music-logic.md).
import type { Chord } from '../theory/chord';
import { QUALITIES } from '../theory/chord';
import { mod } from '../util/math';
import { EXTENDED_SHAPES } from './extendedShapes';
import { difficulty, searchVoicings, type FrettedInstrument } from './voicings';

export { soundingPitchClasses, TUNINGS, type FrettedInstrument } from './voicings';

export type Instrument = FrettedInstrument | 'piano';
/** Instruments with hand-picked (major, minor) and generated (other) default shapes. */
export type TableInstrument = 'guitar' | 'ukulele';

/**
 * Major and minor shapes, hand-picked. Fret per string, low → high; −1 muted, 0 open.
 * Index = root pitch class (C = 0). Other qualities come from extendedShapes.ts (generated).
 */
const TRIAD_SHAPES: Record<TableInstrument, Record<'maj' | 'min', number[][]>> = {
  guitar: {
    maj: [
      [-1, 3, 2, 0, 1, 0],
      [-1, 4, 6, 6, 6, 4],
      [-1, -1, 0, 2, 3, 2],
      [-1, 6, 8, 8, 8, 6],
      [0, 2, 2, 1, 0, 0],
      [1, 3, 3, 2, 1, 1],
      [2, 4, 4, 3, 2, 2],
      [3, 2, 0, 0, 0, 3],
      [4, 6, 6, 5, 4, 4],
      [-1, 0, 2, 2, 2, 0],
      [-1, 1, 3, 3, 3, 1],
      [-1, 2, 4, 4, 4, 2],
    ],
    min: [
      [-1, 3, 5, 5, 4, 3],
      [-1, 4, 6, 6, 5, 4],
      [-1, -1, 0, 2, 3, 1],
      [-1, 6, 8, 8, 7, 6],
      [0, 2, 2, 0, 0, 0],
      [1, 3, 3, 1, 1, 1],
      [2, 4, 4, 2, 2, 2],
      [3, 5, 5, 3, 3, 3],
      [4, 6, 6, 4, 4, 4],
      [-1, 0, 2, 2, 1, 0],
      [-1, 1, 3, 3, 2, 1],
      [-1, 2, 4, 4, 3, 2],
    ],
  },
  ukulele: {
    maj: [
      [0, 0, 0, 3],
      [1, 1, 1, 4],
      [2, 2, 2, 0],
      [0, 3, 3, 1],
      [1, 4, 0, 2],
      [2, 0, 1, 0],
      [3, 1, 2, 1],
      [0, 2, 3, 2],
      [5, 3, 4, 3],
      [2, 1, 0, 0],
      [3, 2, 1, 1],
      [4, 3, 2, 2],
    ],
    min: [
      [0, 3, 3, 3],
      [1, 1, 0, 4],
      [2, 2, 1, 0],
      [3, 3, 2, 1],
      [0, 4, 3, 2],
      [1, 0, 1, 3],
      [2, 1, 2, 0],
      [0, 2, 3, 1],
      [4, 3, 4, 2],
      [2, 0, 0, 0],
      [3, 1, 1, 1],
      [4, 2, 2, 2],
    ],
  },
};

/** Frets for a chord shape (the shape's root, i.e. after transpose − capo). */
export function shapeFrets(
  instrument: FrettedInstrument,
  root: number,
  quality: Chord['quality'],
): number[] {
  if (instrument === 'mandolin' || instrument === 'bass') {
    return searchVoicings(instrument, root, quality)[0] ?? [];
  }
  const table =
    quality === 'maj' || quality === 'min'
      ? TRIAD_SHAPES[instrument][quality]
      : EXTENDED_SHAPES[instrument][quality];
  return table[mod(root, 12)]!;
}

/** Every voicing offered for a chord (F4.9): the default shape first, then other positions. */
export function voicingsFor(
  instrument: FrettedInstrument,
  root: number,
  quality: Chord['quality'],
): number[][] {
  const first = shapeFrets(instrument, root, quality);
  const key = first.join(',');
  return [first, ...searchVoicings(instrument, root, quality).filter((v) => v.join(',') !== key)];
}

/** The easiest voicing (F5.4 easy mode): fewest fingers, no barre if avoidable, low on the neck. */
export function easiestVoicing(
  instrument: FrettedInstrument,
  root: number,
  quality: Chord['quality'],
): number[] {
  const all = voicingsFor(instrument, root, quality);
  return all.reduce((best, v) => (difficulty(v) < difficulty(best) ? v : best), all[0]!);
}

export interface ShapeLayout {
  strings: number;
  /** First fret shown: 1 means the nut is drawn; otherwise label "Nfr". */
  baseFret: number;
  /** Per string (low → high, or mirrored): "×" muted, "o" open, "" fretted. */
  markers: ('×' | 'o' | '')[];
  /** Fret relative to baseFret (1-based) and the string range it covers. */
  barre: { fret: number; fromString: number; toString: number } | null;
  /** Fretted notes not covered by the barre; fret relative to baseFret (1-based). */
  dots: { string: number; fret: number }[];
}

/**
 * Diagram layout: 4 frets shown; if a fret is above 4 the window starts at the lowest fretted
 * fret. A barre is drawn when 2+ strings share the lowest fret, no string is open, and the
 * barre starts on the lowest fretted string.
 */
export function layoutShape(frets: number[]): ShapeLayout {
  const fretted = frets.flatMap((f, s) => (f > 0 ? [{ f, s }] : []));
  const min = fretted.length ? Math.min(...fretted.map((x) => x.f)) : 1;
  const max = fretted.length ? Math.max(...fretted.map((x) => x.f)) : 1;
  const baseFret = max > 4 ? min : 1;
  const covered = fretted.filter((x) => x.f === min);
  const isBarre =
    covered.length >= 2 && !frets.some((f) => f === 0) && covered[0]?.s === fretted[0]?.s;
  return {
    strings: frets.length,
    baseFret,
    markers: frets.map((f) => (f < 0 ? '×' : f === 0 ? 'o' : '')),
    barre: isBarre
      ? { fret: min - baseFret + 1, fromString: covered[0]!.s, toString: covered.at(-1)!.s }
      : null,
    dots: fretted
      .filter((x) => !(isBarre && x.f === min))
      .map((x) => ({ string: x.s, fret: x.f - baseFret + 1 })),
  };
}

/** Left-handed diagrams (F4.10): mirror the string order. */
export function mirrorLayout(layout: ShapeLayout): ShapeLayout {
  const flip = (s: number) => layout.strings - 1 - s;
  return {
    ...layout,
    markers: [...layout.markers].reverse(),
    barre: layout.barre
      ? {
          fret: layout.barre.fret,
          fromString: flip(layout.barre.toString),
          toString: flip(layout.barre.fromString),
        }
      : null,
    dots: layout.dots.map((d) => ({ ...d, string: flip(d.string) })),
  };
}

/**
 * Keys to light on a 2-octave keyboard (0 = first C … 23 = last B). Piano shows sounding
 * pitches: transpose applies, capo does not. The first entry is the root.
 */
export function pianoKeys(chord: Chord | null, transpose: number): number[] {
  if (!chord) return [];
  const root = mod(chord.root + transpose, 12);
  return QUALITIES[chord.quality].intervals.map((iv) => {
    const k = root + iv;
    return k > 23 ? k - 12 : k;
  });
}
